import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Store, Loader2, X } from "lucide-react";

function FeeModal({ merchant, onClose, onSaved }) {
  const { t } = useI18n();
  const [pct, setPct] = useState(merchant.fee_percentage ?? "");
  const [fixed, setFixed] = useState(merchant.fee_fixed ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/merchants/${merchant.id}/fees`, {
        percentage: pct === "" ? null : parseFloat(pct),
        fixed: fixed === "" ? null : parseFloat(fixed),
      });
      toast.success(t("adm.feesSaved")); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm ob-card rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="merchant-fee-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">{t("adm.customFees")} — {merchant.business_name}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-500">{t("adm.gwPct")}</label>
            <input type="number" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} dir="ltr" placeholder="default" data-testid="merchant-fee-pct"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white text-end focus:border-[#00E5FF] outline-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">{t("adm.gwFixed")}</label>
            <input type="number" step="0.01" value={fixed} onChange={(e) => setFixed(e.target.value)} dir="ltr" placeholder="default" data-testid="merchant-fee-fixed"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white text-end focus:border-[#00E5FF] outline-none" />
          </div>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-merchant-fees"
          className="mt-6 w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t("common.save")}
        </button>
      </div>
    </div>
  );
}

export default function AdminMerchants() {
  const { t } = useI18n();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/merchants"); setMerchants(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Store className="text-[#00E5FF]" /> {t("adm.merchantsTitle")}</h1>
        <p className="text-zinc-500 text-sm mt-1">{merchants.length} {t("adm.merchantsTitle")}</p>
      </div>

      <div className="ob-card rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="merchants-table">
          <thead>
            <tr className="text-zinc-500 text-xs border-b border-zinc-900">
              <th className="text-start px-4 py-3 font-medium">{t("common.actions")}</th>
              <th className="text-start px-4 py-3 font-medium">{t("adm.customFees")}</th>
              <th className="text-start px-4 py-3 font-medium">{t("adm.merchantPayments")}</th>
              <th className="text-start px-4 py-3 font-medium">{t("common.currency")}</th>
              <th className="text-end px-4 py-3 font-medium">{t("adm.business")}</th>
            </tr>
          </thead>
          <tbody>
            {merchants.map((m) => (
              <tr key={m.id} className="border-b border-zinc-900/60 hover:bg-white/[0.02]" data-testid={`merchant-row-${m.id.slice(0,8)}`}>
                <td className="px-4 py-3">
                  <button onClick={() => setModal(m)} data-testid={`set-fees-${m.id.slice(0,8)}`}
                    className="text-xs text-[#00E5FF] hover:underline">{t("adm.setFees")}</button>
                </td>
                <td className="px-4 py-3 font-mono-ob text-zinc-400 text-xs" dir="ltr">
                  {m.fee_percentage != null || m.fee_fixed != null ? `${m.fee_percentage ?? "·"}% + ${m.fee_fixed ?? "·"}` : "—"}
                </td>
                <td className="px-4 py-3 font-num text-zinc-300" dir="ltr">{m.payments_count}</td>
                <td className="px-4 py-3 font-mono-ob text-zinc-400" dir="ltr">{m.settlement_currency}</td>
                <td className="px-4 py-3 text-end text-white font-medium">{m.business_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <FeeModal merchant={modal} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
